import { useState, useCallback, useRef, useEffect } from 'react';
import { CITIES } from '@/constants';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  label?: string;
  customCities?: string[];
}

export function CityAutocomplete({
  value,
  onChange,
  onBlur,
  error,
  required = false,
  label = 'Ciudad',
  customCities = [],
}: CityAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Guardamos siempre el ultimo texto escrito, para usarlo en el blur sin
  // agarrar un valor viejo (esa era la causa del bug que pisaba la seleccion).
  const inputValueRef = useRef(inputValue);
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  // Bandera: marca cuando el usuario acaba de elegir una opcion, para que el
  // blur NO vuelva a escribir el texto a medias encima de la seleccion.
  const justSelectedRef = useRef(false);

  // Lista completa: predefinidas + las que el usuario haya agregado.
  const allCities = [...CITIES, ...customCities.filter((c) => !CITIES.includes(c))];

  // Filtro por subcadena (con 2 letras ya basta) y orden: primero las que
  // EMPIEZAN con lo escrito, luego alfabetico.
  const q = inputValue.trim().toLowerCase();
  const filteredCities = q
    ? allCities
        .filter((city) => city.toLowerCase().includes(q))
        .sort((a, b) => {
          const aStarts = a.toLowerCase().startsWith(q) ? 0 : 1;
          const bStarts = b.toLowerCase().startsWith(q) ? 0 : 1;
          if (aStarts !== bStarts) return aStarts - bStarts;
          return a.localeCompare(b);
        })
    : allCities;

  // Mostrar "Agregar" solo si lo escrito no es exactamente una ciudad existente.
  const showAddNew =
    q.length > 0 && !allCities.some((city) => city.toLowerCase() === q);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Si el usuario vuelve a escribir, ya no estamos en "recien seleccionado".
      justSelectedRef.current = false;
      setInputValue(e.target.value);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    []
  );

  const handleSelectCity = useCallback(
    (city: string) => {
      justSelectedRef.current = true;
      setInputValue(city);
      onChange(city);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputBlur = () => {
    setIsOpen(false);

    // Si acabamos de elegir una opcion, NO pisamos el valor seleccionado.
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      onBlur?.();
      return;
    }

    // Si escribio algo y no eligio (ciudad nueva), conservamos lo escrito.
    const latest = inputValueRef.current.trim();
    if (latest) {
      onChange(latest);
    }
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }

    const optionsCount = filteredCities.length + (showAddNew ? 1 : 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < optionsCount - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCities.length) {
          handleSelectCity(filteredCities[highlightedIndex]);
        } else if (highlightedIndex === filteredCities.length && showAddNew) {
          handleSelectCity(inputValue.trim());
        } else if (inputValue.trim()) {
          handleSelectCity(inputValue.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Mantener visible la opcion resaltada al moverse con flechas.
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return (
    <div className="relative w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-danger-500 ml-1">*</span>}
      </label>

      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder="Escribe 2 letras y elige..."
        className={`
          w-full px-4 py-3 rounded-lg border transition-colors duration-200
          placeholder:text-gray-400 text-gray-900
          ${
            error
              ? 'border-danger-500 focus:border-danger-500 focus:ring-2 focus:ring-danger-500/20'
              : 'border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
          }
        `}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        autoComplete="off"
      />

      {/* Dropdown */}
      {isOpen && (filteredCities.length > 0 || showAddNew) && (
        <ul
          ref={listRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {filteredCities.map((city, index) => (
            <li
              key={city}
              // onMouseDown + preventDefault: registra la seleccion ANTES de que
              // el input pierda el foco. Esto evita que el blur pise la eleccion.
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectCity(city);
              }}
              className={`
                px-4 py-2 cursor-pointer transition-colors
                ${
                  index === highlightedIndex
                    ? 'bg-primary-50 text-primary-700'
                    : 'hover:bg-gray-50'
                }
                ${city === value ? 'font-medium text-primary-600' : ''}
              `}
              role="option"
              aria-selected={city === value}
            >
              {city}
            </li>
          ))}

          {showAddNew && (
            <li
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectCity(inputValue.trim());
              }}
              className={`
                px-4 py-2 cursor-pointer transition-colors flex items-center gap-2
                ${
                  highlightedIndex === filteredCities.length
                    ? 'bg-primary-50 text-primary-700'
                    : 'hover:bg-gray-50 text-gray-600'
                }
              `}
              role="option"
              aria-selected={false}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Agregar "{inputValue.trim()}"
            </li>
          )}
        </ul>
      )}

      {error && (
        <p className="mt-1 text-sm text-danger-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
